import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import classnames from 'classnames';
import {
  UncontrolledButtonDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  UncontrolledTooltip,
} from 'reactstrap';
import { useIntl } from 'react-intl';
import Skeleton from 'react-loading-skeleton';

import {
  setSelectedCollections,
  setSelectedAllCollections,
} from '../../actions/Collections';
import {
  setSelectedFiles,
  setSelectedAllFiles,
} from '../../actions/Files';

const HeaderSelectAll = ({
  files,
  filesLoading,
  filesTotal,
  selectedFiles,
  selectedAllFiles,
  collection,
  collections,
  collectionsTotal,
  selectedCollections,
  selectedAllCollections,
  selectVisibleFiles,
  selectAllFiles,
  unselectAllFiles,
  selectVisibleCollections,
  selectAllCollections,
  unselectAllCollections,
  reorderingMode,
}) => {
  const intl = useIntl();

  if (reorderingMode || collection) {
    return null;
  }

  const loadedFiles = files.length;
  const loadedCollections = collections.length;

  const isSelectAllChecked = () => (
    (files.length > 0 || collections.length > 0) &&
      ((files.length === selectedFiles.length && files.length > 0) || files.length === 0) &&
      ((collections.length === selectedCollections.length && collections.length > 0) ||
        collections.length === 0)
  );

  const getLoadedFilesLabel = () => {
    if (filesLoading) {
      return <Skeleton width={90} />;
    }

    if (filesTotal === 0 || reorderingMode) {
      return null;
    }

    return intl.formatMessage(
      { defaultMessage: '{loadedFiles} of {filesTotal} files' },
      { loadedFiles, filesTotal },
    );
  };

  const label = (collectionsCount, filesCount) => {
    const labels = [];
    if (collectionsCount > 0) {
      labels.push(intl.formatMessage(
        { defaultMessage: 'Collections ({collectionsCount})' },
        { collectionsCount },
      ));
    }
    if (filesCount > 0) {
      labels.push(intl.formatMessage(
        { defaultMessage: 'files ({filesCount})' },
        { filesCount },
      ));
    }
    return labels.join(intl.formatMessage({ defaultMessage: '{nbsp}and{nbsp}' }, { nbsp: ' ' }));
  };

  const loadedLabel = () => (
    label(loadedCollections, loadedFiles)
  );

  const totalLabel = () => (
    label(collectionsTotal, filesTotal)
  );

  const selectAllDisabled = filesTotal > 10000 || collectionsTotal > 10000;
  const showSelectAll = !collection &&
    (filesTotal > loadedFiles || collectionsTotal > loadedCollections);

  return (
    <div className="files-displaying select-all-files-collections">
      <label htmlFor="FilesCollectionsSelectAll" id="FilesSelectAllLabel">
        {!collection ? (
          getLoadedFilesLabel()
        ) : (
          intl.formatMessage({ defaultMessage: 'Select' })
        )}
      </label>

      <UncontrolledButtonDropdown>
        <DropdownToggle id="FilesCollectionsSelectAll" caret size="sm" color="secondary" className="select-all-button" />
        <DropdownMenu>
          <DropdownItem
            onClick={() => {
              if (isSelectAllChecked() && !selectedAllFiles) {
                unselectAllFiles();
              } else {
                selectVisibleFiles(files);
                selectAllFiles(false);
              }
              if (isSelectAllChecked() && !selectedAllCollections) {
                unselectAllCollections();
              } else {
                selectVisibleCollections(collections);
                selectAllCollections(false);
              }
            }}
          >
            {(isSelectAllChecked() && !selectedAllFiles && !selectedAllCollections) && (
              <i className="far fa-check" />
            )}
            {intl.formatMessage({ defaultMessage: 'Select visible {label}' }, { label: loadedLabel() })}
          </DropdownItem>
          {showSelectAll && (
            <DropdownItem
              id="select-all-dropdown-item"
              className={classnames({ inactive: selectAllDisabled })}
              onClick={() => {
                if (selectAllDisabled) {
                  return;
                }

                if (selectedAllFiles) {
                  unselectAllFiles();
                } else if (files.length > 0) {
                  selectAllFiles(true);
                  selectVisibleFiles(files);
                }
                if (selectedAllCollections) {
                  unselectAllCollections();
                } else if (collections.length > 0) {
                  selectAllCollections(true);
                  selectVisibleCollections(collections);
                }
              }}
            >
              {(selectedAllFiles && selectedAllCollections) && (
                <i className="far fa-check" />
              )}
              {intl.formatMessage({ defaultMessage: 'Select all {label}' }, { label: totalLabel() })}
            </DropdownItem>
          )}
          {selectAllDisabled && (
            <UncontrolledTooltip fade={false} target="select-all-dropdown-item">
              {intl.formatMessage({
                defaultMessage: `You can't select more than 10,000 files and/or Collections.
                  Please use filters to refine your search.`,
              })}
            </UncontrolledTooltip>
          )}
          {(isSelectAllChecked() || selectedAllFiles || selectedAllCollections) && (
            <DropdownItem
              onClick={() => {
                unselectAllFiles();
                selectAllFiles(false);
                unselectAllCollections();
                selectAllCollections(false);
              }}
            >
              {intl.formatMessage({ defaultMessage: 'Unselect all' })}
            </DropdownItem>
          )}
        </DropdownMenu>
      </UncontrolledButtonDropdown>
    </div>
  );
};

const mapStateToProps = state => ({
  profile: state.Auth.profile,
  filesLoading: state.Files.loading,
  filesParams: state.Files.params,
  filters: state.Files.filters,
  files: state.Files.files,
  filesTotal: state.Files.filesTotal,
  selectedFiles: state.Files.selectedFiles,
  selectedAllFiles: state.Files.selectedAllFiles,
  collection: state.Collections.collection,
  collectionLoading: state.Collections.collectionLoading,
  collections: state.Collections.collections,
  collectionsTotal: state.Collections.collectionsTotal,
  selectedCollections: state.Collections.selectedCollections,
  selectedAllCollections: state.Collections.selectedAllCollections,
  filesGroup: state.Files.filesGroup,
  download: state.Files.download,
  reorderingMode: state.Files.reorderingMode,
});

const mapDispatchToProps = dispatch => ({
  selectVisibleFiles: (files) => {
    dispatch(setSelectedFiles(files));
  },
  selectAllFiles: (selectAllFiles) => {
    dispatch(setSelectedAllFiles(selectAllFiles));
  },
  unselectAllFiles: () => {
    dispatch(setSelectedFiles([]));
    dispatch(setSelectedAllFiles(false));
  },
  selectVisibleCollections: (collections) => {
    dispatch(setSelectedCollections(collections));
  },
  selectAllCollections: (selectAllCollections) => {
    dispatch(setSelectedAllCollections(selectAllCollections));
  },
  unselectAllCollections: () => {
    dispatch(setSelectedCollections([]));
  },
});

HeaderSelectAll.propTypes = {
  files: PropTypes.array.isRequired,
  filesLoading: PropTypes.bool.isRequired,
  filesTotal: PropTypes.number.isRequired,
  selectedFiles: PropTypes.array.isRequired,
  selectedAllFiles: PropTypes.bool.isRequired,
  collection: PropTypes.object,
  collections: PropTypes.array.isRequired,
  collectionsTotal: PropTypes.number.isRequired,
  selectedCollections: PropTypes.array.isRequired,
  selectedAllCollections: PropTypes.bool.isRequired,
  selectVisibleFiles: PropTypes.func.isRequired,
  selectAllFiles: PropTypes.func.isRequired,
  unselectAllFiles: PropTypes.func.isRequired,
  selectVisibleCollections: PropTypes.func.isRequired,
  selectAllCollections: PropTypes.func.isRequired,
  unselectAllCollections: PropTypes.func.isRequired,
  reorderingMode: PropTypes.bool.isRequired,
};

HeaderSelectAll.defaultProps = {
  collection: null,
};

export default connect(mapStateToProps, mapDispatchToProps)(HeaderSelectAll);
